class PageContentWithMenu {
  constructor(root) {
    this.root = root;
    this.toggleButton = root.querySelector('[data-page-menu-toggle]');
    this.menuWrapper = root.querySelector('[data-page-menu-wrapper]');
    this.teamGrid = root.querySelector('[data-team-grid]');
    this.teamDetailsContainer = root.querySelector('[data-team-top]');
    this.teamTriggers = this.teamGrid ? Array.from(this.teamGrid.querySelectorAll('[data-member-trigger]')) : [];
    this.teamDetails = this.teamDetailsContainer ? Array.from(this.teamDetailsContainer.querySelectorAll('[data-member-details]')) : [];

    this.onToggleClick = this.onToggleClick.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onTeamTriggerClick = this.onTeamTriggerClick.bind(this);
  }

  init() {
    if (!this.toggleButton || !this.menuWrapper) {
      this.initTeamMembers();
      return;
    }

    this.toggleButton.addEventListener('click', this.onToggleClick);
    window.addEventListener('resize', this.onResize, { passive: true });
    this.syncStateForViewport();
    this.initTeamMembers();
  }

  destroy() {
    if (this.toggleButton) {
      this.toggleButton.removeEventListener('click', this.onToggleClick);
    }

    window.removeEventListener('resize', this.onResize);

    this.teamTriggers.forEach((trigger) => {
      trigger.removeEventListener('click', this.onTeamTriggerClick);
    });
  }

  onToggleClick() {
    const isOpen = this.menuWrapper.classList.contains('is-open');
    if (isOpen) {
      this.collapseMobileMenu();
    } else {
      this.expandMobileMenu();
    }
  }

  onResize() {
    this.syncStateForViewport();
  }

  syncStateForViewport() {
    const isMobile = window.matchMedia('(max-width: 768px)').matches;

    if (!isMobile) {
      this.menuWrapper.classList.remove('is-open');
      this.menuWrapper.style.height = '';
      this.toggleButton.classList.remove('open');
      this.toggleButton.setAttribute('aria-expanded', 'false');
      return;
    }

    const currentlyOpen = this.menuWrapper.classList.contains('is-open');
    if (currentlyOpen) {
      this.menuWrapper.style.height = `${this.menuWrapper.scrollHeight}px`;
    } else {
      this.menuWrapper.style.height = '0px';
    }

    this.toggleButton.classList.toggle('open', currentlyOpen);
    this.toggleButton.setAttribute('aria-expanded', currentlyOpen ? 'true' : 'false');
  }

  expandMobileMenu() {
    const startHeight = this.menuWrapper.getBoundingClientRect().height;
    this.menuWrapper.classList.add('is-open');
    this.menuWrapper.style.height = `${startHeight}px`;

    const targetHeight = this.menuWrapper.scrollHeight;
    window.requestAnimationFrame(() => {
      this.menuWrapper.style.height = `${targetHeight}px`;
    });

    this.toggleButton.classList.add('open');
    this.toggleButton.setAttribute('aria-expanded', 'true');

    window.setTimeout(() => {
      if (this.menuWrapper.classList.contains('is-open')) {
        this.menuWrapper.style.height = 'auto';
      }
    }, 270);
  }

  collapseMobileMenu() {
    const startHeight = this.menuWrapper.scrollHeight;
    this.menuWrapper.style.height = `${startHeight}px`;

    window.requestAnimationFrame(() => {
      this.menuWrapper.style.height = '0px';
    });

    this.menuWrapper.classList.remove('is-open');
    this.toggleButton.classList.remove('open');
    this.toggleButton.setAttribute('aria-expanded', 'false');
  }

  initTeamMembers() {
    if (!this.teamTriggers.length || !this.teamDetails.length) {
      return;
    }

    this.teamTriggers.forEach((trigger) => {
      trigger.addEventListener('click', this.onTeamTriggerClick);
      trigger.setAttribute('aria-pressed', 'false');
    });
  }

  onTeamTriggerClick(event) {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) {
      return;
    }

    this.scrollToTeamTop();
    this.setActiveMember(trigger.dataset.memberTrigger || null);
  }

  setActiveMember(memberId) {
    if (!memberId) {
      return;
    }

    this.teamTriggers.forEach((trigger) => {
      const isActive = trigger.dataset.memberTrigger === memberId;
      trigger.classList.toggle('is-active', isActive);
      trigger.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    this.teamDetails.forEach((detail) => {
      const isOpen = detail.dataset.memberDetails === memberId;
      detail.classList.toggle('is-active', isOpen);

      if (isOpen) {
        this.slideDown(detail);
      } else {
        this.slideUp(detail);
      }
    });
  }

  slideDown(element, duration = 280) {
    if (!(element instanceof HTMLElement)) {
      return;
    }

    if (element.classList.contains('is-open')) {
      return;
    }

    element.classList.add('is-open');
    element.style.display = 'block';
    element.style.overflow = 'hidden';
    element.style.height = '0px';

    const targetHeight = element.scrollHeight;

    window.requestAnimationFrame(() => {
      element.style.transition = `height ${duration}ms cubic-bezier(0.215, 0.61, 0.355, 1)`;
      element.style.height = `${targetHeight}px`;
    });

    window.setTimeout(() => {
      element.style.height = '';
      element.style.overflow = '';
      element.style.transition = '';
    }, duration);
  }

  slideUp(element, duration = 240) {
    if (!(element instanceof HTMLElement)) {
      return;
    }

    if (!element.classList.contains('is-open')) {
      element.style.display = 'none';
      return;
    }

    const startHeight = element.scrollHeight;
    element.style.overflow = 'hidden';
    element.style.height = `${startHeight}px`;

    window.requestAnimationFrame(() => {
      element.style.transition = `height ${duration}ms cubic-bezier(0.215, 0.61, 0.355, 1)`;
      element.style.height = '0px';
    });

    window.setTimeout(() => {
      element.classList.remove('is-open');
      element.style.display = 'none';
      element.style.height = '';
      element.style.overflow = '';
      element.style.transition = '';
    }, duration);
  }

  scrollToTeamTop() {
    if (!(this.teamDetailsContainer instanceof HTMLElement)) {
      return;
    }

    const offset = 80;
    const pageWrapper = document.querySelector('.page-wrapper');

    if (pageWrapper instanceof HTMLElement && pageWrapper.scrollHeight > pageWrapper.clientHeight + 1) {
      const wrapperRect = pageWrapper.getBoundingClientRect();
      const detailsRect = this.teamDetailsContainer.getBoundingClientRect();
      const target = pageWrapper.scrollTop + (detailsRect.top - wrapperRect.top) - offset;
      pageWrapper.scrollTop = Math.max(target, 0);
      return;
    }

    const target = this.teamDetailsContainer.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo(0, Math.max(target, 0));
  }
}

const pageContentMenuInstances = new Map();

function initPageContentMenus(scope) {
  const root = scope instanceof HTMLElement || scope instanceof Document ? scope : document;
  const nodes = root.querySelectorAll('[data-page-content-menu]');

  nodes.forEach((node) => {
    if (pageContentMenuInstances.has(node)) {
      return;
    }

    const instance = new PageContentWithMenu(node);
    instance.init();
    pageContentMenuInstances.set(node, instance);
  });
}

function destroyPageContentMenus(scope) {
  if (!(scope instanceof HTMLElement)) {
    return;
  }

  const nodes = scope.querySelectorAll('[data-page-content-menu]');
  nodes.forEach((node) => {
    const instance = pageContentMenuInstances.get(node);
    if (!instance) {
      return;
    }

    instance.destroy();
    pageContentMenuInstances.delete(node);
  });

  if (scope.matches('[data-page-content-menu]')) {
    const instance = pageContentMenuInstances.get(scope);
    if (instance) {
      instance.destroy();
      pageContentMenuInstances.delete(scope);
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initPageContentMenus(document);
  });
} else {
  initPageContentMenus(document);
}

document.addEventListener('shopify:section:load', (event) => {
  initPageContentMenus(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyPageContentMenus(event.target);
});
